import {
    createSlice,
    createAsyncThunk,
} from "@reduxjs/toolkit";

import { apiFetch } from "../../utils";

/*
 * Fetch conversations
 */
export const fetchConversations =
    createAsyncThunk(
        "chat/fetchConversations",
        async (_, { rejectWithValue }) => {
            try {
                const response =
                    await apiFetch(
                        "chat/p2p"
                    );

                if (!response.ok) {
                    throw new Error(
                        "Failed to fetch conversations"
                    );
                }

                return await response.json();
            } catch (error) {
                return rejectWithValue(
                    error.message ||
                    "Failed to fetch conversations"
                );
            }
        }
    );

/*
 * Fetch messages for a P2P conversation
 */
export const fetchConversationMessages =
    createAsyncThunk(
        "chat/fetchConversationMessages",
        async (
            p2pId,
            { rejectWithValue }
        ) => {
            try {
                const response =
                    await apiFetch(
                        `chat/p2p/${p2pId}/messages`
                    );

                if (!response.ok) {
                    throw new Error(
                        "Failed to fetch conversation messages"
                    );
                }

                const data =
                    await response.json();

                return {
                    p2pId,
                    userId:
                        data.userid,
                    messages:
                        data.messages ?? [],
                };
            } catch (error) {
                return rejectWithValue(
                    error.message ||
                    "Failed to fetch conversation messages"
                );
            }
        }
    );

/*
 * Search user by email
 */
export const searchUserByEmail =
    createAsyncThunk(
        "chat/searchUserByEmail",
        async (
            email,
            { rejectWithValue }
        ) => {
            try {
                const response =
                    await apiFetch(
                        `auth/service/chat/user/${encodeURIComponent(
                            email
                        )}`
                    );

                if (
                    response.status ===
                    404
                ) {
                    return null;
                }

                if (!response.ok) {
                    throw new Error(
                        "Failed to search user"
                    );
                }

                return await response.json();
            } catch (error) {
                return rejectWithValue(
                    error.message ||
                    "Failed to search user"
                );
            }
        }
    );

/*
 * Create P2P conversation
 *
 * This should ONLY be dispatched when
 * the first message is being sent.
 */
export const createP2PConversation =
    createAsyncThunk(
        "chat/createP2PConversation",
        async (
            participantId,
            { rejectWithValue }
        ) => {
            try {
                const response =
                    await apiFetch(
                        "chat/p2p",
                        {
                            method: "POST",
                            body: JSON.stringify({
                                participantId,
                            }),
                        }
                    );

                if (!response.ok) {
                    throw new Error(
                        "Failed to create conversation"
                    );
                }

                return await response.json();
            } catch (error) {
                return rejectWithValue(
                    error.message ||
                    "Failed to create conversation"
                );
            }
        }
    );

/*
 * Slice
 */
const chatSlice = createSlice({
    name: "chat",

    initialState: {
        currentUserId: null,

        conversations: [],

        selectedConversationId:
            null,

        /*
         * User selected through
         * "New Chat" but conversation
         * does not exist yet.
         */
        pendingUser: null,

        /*
         * Temporary conversation displayed
         * before the first message creates
         * the real conversation.
         */
        pendingConversation: null,

        foundUser: null,

        loading: false,
        loadingMessages: false,
        searchingUser: false,
        creatingConversation: false,

        error: null,
        notice: null,
    },

    reducers: {
        clearChatError: (state) => {
            state.error = null;
        },

        clearChatNotice: (state) => {
            state.notice = null;
        },

        clearFoundUser: (state) => {
            state.foundUser = null;
        },

        setFoundUser: (
            state,
            action
        ) => {
            state.foundUser =
                action.payload;
        },

        /*
         * User clicked "Message".
         *
         * No API call.
         */
        startNewChat: (
            state,
            action
        ) => {
            state.pendingUser =
                action.payload;

            state.pendingConversation =
                null;

            state.selectedConversationId =
                null;

            state.foundUser = null;
        },

        /*
         * Select an existing conversation.
         */
        selectConversation: (
            state,
            action
        ) => {
            state.selectedConversationId =
                action.payload;

            state.pendingUser = null;

            state.pendingConversation =
                null;
        },

        /*
         * Store temporary conversation after
         * first message creates it.
         */
        setPendingConversation: (
            state,
            action
        ) => {
            state.pendingConversation =
                action.payload;
        },

        /*
         * Add/update conversation.
         */
        upsertConversation: (
            state,
            action
        ) => {
            const conversation =
                action.payload;

            const index =
                state.conversations.findIndex(
                    (item) =>
                        String(item.id) ===
                        String(
                            conversation.id
                        )
                );

            if (index === -1) {
                state.conversations.unshift(
                    conversation
                );
            } else {
                state.conversations[
                    index
                ] = conversation;
            }
        },

        /*
         * Remove temporary state after
         * real conversation exists.
         */
        clearPendingChat: (
            state
        ) => {
            state.pendingUser = null;

            state.pendingConversation =
                null;
        },

        /*
         * =====================================================
         * Realtime message
         *
         * Payload:
         *
         * {
         *     conversationId,
         *     message
         * }
         * =====================================================
         */
        updateConversationMessage: (
            state,
            action
        ) => {
            const {
                conversationId,
                message,
            } = action.payload;

            if (
                !conversationId ||
                !message
            ) {
                return;
            }

            const conversation =
                state.conversations.find(
                    (item) =>
                        String(item.id) ===
                        String(
                            conversationId
                        )
                );

            if (!conversation) {
                return;
            }

            if (
                !conversation.messages
            ) {
                conversation.messages =
                    [];
            }

            /*
             * Prevent duplicate realtime
             * messages.
             */
            const exists =
                conversation.messages.some(
                    (item) => {
                        if (
                            item.id &&
                            message.id
                        ) {
                            return (
                                String(
                                    item.id
                                ) ===
                                String(
                                    message.id
                                )
                            );
                        }

                        if (
                            item.clientMessageId &&
                            message.clientMessageId
                        ) {
                            return (
                                item.clientMessageId ===
                                message.clientMessageId
                            );
                        }

                        return false;
                    }
                );

            if (exists) {
                return;
            }

            conversation.messages.push(
                message
            );

            conversation.lastMessage =
                message.contents?.find(
                    (content) =>
                        content.type === 1
                )?.text ?? "";

            conversation.lastMessageAt =
                message.createdAt;
        },
    },

    extraReducers: (
        builder
    ) => {
        builder

            /*
             * =================================================
             * Conversations
             * =================================================
             */

            .addCase(
                fetchConversations.pending,
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )

            .addCase(
                fetchConversations.fulfilled,
                (
                    state,
                    action
                ) => {
                    state.loading =
                        false;

                    state.conversations =
                        action.payload ||
                        [];
                }
            )

            .addCase(
                fetchConversations.rejected,
                (
                    state,
                    action
                ) => {
                    state.loading =
                        false;

                    state.error =
                        action.payload ||
                        "Failed to load conversations";
                }
            )

            /*
             * =================================================
             * Conversation messages
             * =================================================
             */

            .addCase(
                fetchConversationMessages.pending,
                (state) => {
                    state.loadingMessages =
                        true;

                    state.error = null;
                }
            )

            .addCase(
                fetchConversationMessages.fulfilled,
                (
                    state,
                    action
                ) => {
                    state.loadingMessages =
                        false;

                    const {
                        p2pId,
                        userId,
                        messages,
                    } =
                        action.payload;

                    /*
                     * Current authenticated user.
                     */
                    state.currentUserId =
                        userId;

                    const conversation =
                        state.conversations.find(
                            (item) =>
                                String(
                                    item.id
                                ) ===
                                String(
                                    p2pId
                                )
                        );

                    if (
                        !conversation
                    ) {
                        return;
                    }

                    /*
                     * Replace history.
                     */
                    conversation.messages =
                        messages || [];
                }
            )

            .addCase(
                fetchConversationMessages.rejected,
                (
                    state,
                    action
                ) => {
                    state.loadingMessages =
                        false;

                    state.error =
                        action.payload ||
                        "Failed to load conversation messages";
                }
            )

            /*
             * =================================================
             * Search user
             * =================================================
             */

            .addCase(
                searchUserByEmail.pending,
                (state) => {
                    state.searchingUser =
                        true;

                    state.error = null;

                    state.foundUser =
                        null;
                }
            )

            .addCase(
                searchUserByEmail.fulfilled,
                (
                    state,
                    action
                ) => {
                    state.searchingUser =
                        false;

                    state.foundUser =
                        action.payload;

                    if (
                        !action.payload
                    ) {
                        state.error =
                            "No user found with that email.";
                    }
                }
            )

            .addCase(
                searchUserByEmail.rejected,
                (
                    state,
                    action
                ) => {
                    state.searchingUser =
                        false;

                    state.error =
                        action.payload ||
                        "Failed to search user";
                }
            )

            /*
             * =================================================
             * Create P2P conversation
             * =================================================
             */

            .addCase(
                createP2PConversation.pending,
                (state) => {
                    state.creatingConversation =
                        true;

                    state.error = null;
                }
            )

            .addCase(
                createP2PConversation.fulfilled,
                (
                    state,
                    action
                ) => {
                    state.creatingConversation =
                        false;

                    const conversation =
                        action.payload;

                    const index =
                        state.conversations.findIndex(
                            (item) =>
                                String(
                                    item.id
                                ) ===
                                String(
                                    conversation.id
                                )
                        );

                    if (index === -1) {
                        state.conversations.unshift(
                            conversation
                        );
                    } else {
                        state.conversations[
                            index
                        ] = conversation;
                    }

                    state.selectedConversationId =
                        conversation.id;

                    state.pendingUser =
                        null;

                    state.pendingConversation =
                        null;

                    state.error = null;
                }
            )

            .addCase(
                createP2PConversation.rejected,
                (
                    state,
                    action
                ) => {
                    state.creatingConversation =
                        false;

                    state.error =
                        action.payload ||
                        "Failed to create conversation";
                }
            );
    },
});

export const {
    clearChatError,
    clearChatNotice,
    clearFoundUser,
    setFoundUser,
    startNewChat,
    selectConversation,
    setPendingConversation,
    upsertConversation,
    clearPendingChat,
    updateConversationMessage,
} = chatSlice.actions;

export default chatSlice.reducer;